import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import {
  ArrowLeft, ArrowRight, Clock, CheckCircle2, XCircle, Trophy,
  GraduationCap, RotateCcw, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function QuizTake() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const startRef = useRef(Date.now());
  const submittedRef = useRef(false);

  useEffect(() => {
    api
      .get(`/quizzes/${quizId}`)
      .then((r) => {
        setQuiz(r.data.quiz);
        setTimeLeft(r.data.quiz.time_limit);
        startRef.current = Date.now();
      })
      .catch(() => {
        toast.error("Quiz not found");
        navigate("/quizzes");
      });
  }, [quizId, navigate]);

  const doSubmit = async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    const timeTaken = Math.round((Date.now() - startRef.current) / 1000);
    try {
      const r = await api.post(`/quizzes/${quizId}/submit`, { answers, time_taken: timeTaken });
      setResult(r.data);
      if (auto) toast.info("Time's up — your quiz was submitted automatically.");
    } catch (e) {
      submittedRef.current = false;
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  // countdown timer
  useEffect(() => {
    if (!quiz || result) return;
    if (timeLeft <= 0) {
      doSubmit(true);
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [quiz, timeLeft, result]);

  if (!quiz) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading quiz…</div>;

  // ---------------- Results view ----------------
  if (result) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto p-6 md:p-10" data-testid="quiz-result">
          <div className={`rounded-xl border p-8 text-center ${result.passed ? "border-success/40 bg-success/5" : "border-amber-500/40 bg-amber-500/5"}`}>
            <div className={`mx-auto h-16 w-16 rounded-full grid place-items-center mb-4 ${result.passed ? "bg-success/15 text-success" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}>
              {result.passed ? <Trophy className="h-8 w-8" /> : <RotateCcw className="h-8 w-8" />}
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="quiz-result-score">{result.score_pct}%</h1>
            <p className="text-muted-foreground mt-1">
              You answered <span className="font-semibold text-foreground">{result.correct}</span> of {result.total} correctly.
            </p>
            <div className={`mt-3 inline-block text-sm font-semibold px-3 py-1 rounded-full ${result.passed ? "bg-success/15 text-success" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}>
              {result.passed ? "Passed" : `Not passed — need ${result.pass_pct}%`}
            </div>
            {result.progress?.xp_awarded > 0 && (
              <div className="mt-3 text-sm font-mono text-primary" data-testid="quiz-xp-awarded">+{result.progress.xp_awarded} XP earned</div>
            )}
          </div>

          <h2 className="font-display font-bold text-lg mt-8 mb-3">Review</h2>
          <div className="space-y-4">
            {result.review.map((q, i) => (
              <div key={q.id} className="rounded-lg border border-border bg-card p-5" data-testid={`quiz-review-${q.id}`}>
                <div className="flex items-start gap-2">
                  {q.is_correct ? <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />}
                  <p className="font-medium text-sm">{i + 1}. {q.prompt}</p>
                </div>
                <div className="mt-3 space-y-1.5 pl-7">
                  {q.options.map((opt, oi) => {
                    const isCorrect = oi === q.correct_answer;
                    const isSelected = oi === q.selected;
                    return (
                      <div
                        key={oi}
                        className={`text-sm px-3 py-1.5 rounded border ${
                          isCorrect
                            ? "border-success/50 bg-success/10 text-foreground"
                            : isSelected
                            ? "border-rose-500/50 bg-rose-500/10 text-foreground"
                            : "border-transparent text-muted-foreground"
                        }`}
                      >
                        {opt}
                        {isCorrect && <span className="ml-2 text-xs text-success font-semibold">correct</span>}
                        {isSelected && !isCorrect && <span className="ml-2 text-xs text-rose-500 font-semibold">your answer</span>}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-3 pl-7 italic">{q.explanation}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-8">
            <Button variant="outline" className="flex-1 gap-2" onClick={() => navigate("/quizzes")} data-testid="quiz-back-btn">
              <ArrowLeft className="h-4 w-4" /> Back to quizzes
            </Button>
            <Button
              className="flex-1 gap-2"
              data-testid="quiz-retry-btn"
              onClick={() => {
                setResult(null);
                setAnswers({});
                setIdx(0);
                setTimeLeft(quiz.time_limit);
                startRef.current = Date.now();
                submittedRef.current = false;
              }}
            >
              <RotateCcw className="h-4 w-4" /> Retake quiz
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- Taking view ----------------
  const q = quiz.questions[idx];
  const answered = Object.keys(answers).length;
  const lowTime = timeLeft <= 30;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/quizzes")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="quiz-exit-btn">
            <ArrowLeft className="h-4 w-4" /> Exit
          </button>
          <div className="flex items-center gap-2 font-display font-bold text-sm">
            <GraduationCap className="h-4 w-4 text-primary" /> {quiz.title}
          </div>
          <div className={`flex items-center gap-1.5 font-mono text-sm px-2.5 py-1 rounded ${lowTime ? "bg-rose-500/15 text-rose-500 animate-pulse" : "bg-secondary text-secondary-foreground"}`} data-testid="quiz-timer">
            <Clock className="h-3.5 w-3.5" /> {fmt(timeLeft)}
          </div>
        </div>
        <Progress value={(100 * (idx + 1)) / quiz.questions.length} className="h-1 rounded-none" />
      </header>

      <div className="max-w-3xl mx-auto p-6 md:p-10">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Question {idx + 1} of {quiz.questions.length}</span>
          <span>{answered}/{quiz.questions.length} answered</span>
        </div>

        <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight mt-3" data-testid="quiz-question-prompt">{q.prompt}</h2>

        <div className="mt-6 space-y-3">
          {q.options.map((opt, oi) => {
            const selected = answers[q.id] === oi;
            return (
              <button
                key={oi}
                data-testid={`quiz-option-${oi}`}
                onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                className={`w-full text-left px-4 py-3.5 rounded-lg border text-sm transition-colors flex items-center gap-3 ${
                  selected ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card hover:border-primary/40 hover:bg-accent"
                }`}
              >
                <span className={`h-5 w-5 rounded-full border grid place-items-center shrink-0 text-[11px] font-mono ${selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground text-muted-foreground"}`}>
                  {String.fromCharCode(65 + oi)}
                </span>
                {opt}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-8">
          <Button variant="outline" className="gap-2" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)} data-testid="quiz-prev-btn">
            <ArrowLeft className="h-4 w-4" /> Previous
          </Button>
          {idx < quiz.questions.length - 1 ? (
            <Button className="gap-2" onClick={() => setIdx((i) => i + 1)} data-testid="quiz-next-btn">
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button className="gap-2" onClick={() => doSubmit(false)} disabled={submitting} data-testid="quiz-submit-btn">
              <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit quiz"}
            </Button>
          )}
        </div>

        {/* Question jump grid */}
        <div className="flex flex-wrap gap-2 mt-8 justify-center">
          {quiz.questions.map((qq, i) => (
            <button
              key={qq.id}
              onClick={() => setIdx(i)}
              data-testid={`quiz-jump-${i}`}
              className={`h-8 w-8 rounded-md text-xs font-mono border transition-colors ${
                i === idx ? "border-primary bg-primary text-primary-foreground" : answers[qq.id] != null ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
